<script setup lang="ts">
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import { useDatabaseStore } from '@/stores/databaseStore'
import { computed, ref } from 'vue'
import type { DataTableColumn, DataTableGroup } from './BaseDataTable.vue'
import BaseDataTable from './BaseDataTable.vue'
import BaseTabs from './BaseTabs.vue'

const databaseStore = useDatabaseStore()

const divisionRows = computed(() => {
  return databaseStore.itemDivisions?.filter((d) => d.parent_division_id === null) ?? []
})

type DivisionRow = (typeof divisionRows.value)[number]

const divisionColumns = computed<DataTableColumn<DivisionRow>[]>(() => {
  return [
    {
      name: 'Code',
      columnDataKey: 'code',
      required: true,
      defaultWidth: 75,
    },
    {
      name: 'Name',
      columnDataKey: 'name',
      required: true,
      defaultWidth: 150,
    },
  ]
})

const subdivisionRows = computed(() => {
  return databaseStore.itemDivisions?.filter((d) => d.parent_division_id !== null) ?? []
})

type SubdivisionRow = (typeof subdivisionRows.value)[number]

const subdivisionColumns = computed<DataTableColumn<SubdivisionRow>[]>(() => {
  return [
    {
      name: 'Disivion',
      columnDataKey: 'parent_division_id',
      required: true,
      columnType: 'select',
      selectOptions: () =>
        databaseStore.itemDivisions
          ?.filter((d) => !d.parent_division_id)
          .map((d) => {
            return {
              value: d.id,
              name: `${d.code} ${d.name}`,
            }
          }),
      importConversion: (value) => {
        const division = databaseStore.itemDivisions?.find((d) => d.code === value)
        if (!division)
          throw toastThrowError(`Unable to find division ${value} to import into the Divion column`)
        return division.id
      },
      textDisplayFunction: (value) => {
        const d = databaseStore.itemDivisions?.find((d) => d.id === value)
        return `${d?.code} ${d?.name}`
      },
      defaultWidth: 150,
    },
    {
      name: 'Code',
      columnDataKey: 'code',
      required: true,
      defaultWidth: 75,
    },
    {
      name: 'Name',
      columnDataKey: 'name',
      required: true,
      defaultWidth: 150,
    },
  ]
})

const subdivisionGroups: DataTableGroup<SubdivisionRow>[] = [
  {
    name: 'Division',
    fn: (row) => {
      const division = databaseStore.itemDivisions?.find((d) => d.id === row.parent_division_id)
      if (!division) throw toastThrowError('Unable to find division')
      return `${division.code} ${division.name}`
    },
  },
]

async function createDivision(row: DivisionRow) {
  const result = await supabase
    .from('item_division')
    .insert({
      id: uuidv7(),
      code: row.code,
      name: row.name,
      parent_division_id: row.parent_division_id,
    })
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to add division', result.error)
  databaseStore.itemDivisions?.push(result.data)
}

async function updateDivision(row: DivisionRow) {
  const result = await supabase
    .from('item_division')
    .update({
      code: row.code,
      name: row.name,
    })
    .eq('id', row.id)
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to update division', result.error)
  const index = databaseStore.itemDivisions?.findIndex((d) => d.id === row.id)
  if (index === undefined) throw toastThrowError('Unable to update division. Reload page.')
  databaseStore.itemDivisions?.splice(index, 1, result.data)
}

async function deleteDivision(divisionId: string) {
  const confirmed = confirm('Are you sure you want to delete the database items division?')
  if (!confirmed) return
  const result = await supabase.from('item_division').delete().eq('id', divisionId)
  if (result.error)
    throw toastThrowError(
      'Unable to delete division. Divisions cannot be deleted if they have estimates or items that depend on them.',
      result.error,
    )
  const index = databaseStore.itemDivisions?.findIndex((d) => d.id === divisionId)
  if (index === undefined) throw toastThrowError('Unable to update. Reload page.')
  databaseStore.itemDivisions?.splice(index, 1)
}

const tabs = ['Divisions', 'Subdivisions'] as const
const activeTab = ref<(typeof tabs)[number]>('Divisions')
</script>
<template>
  <div class="flex h-full w-full flex-col">
    <BaseTabs v-model="activeTab" :tabs="tabs" />
    <div v-if="activeTab === 'Divisions'">
      <BaseDataTable
        :rows="divisionRows"
        :columns="divisionColumns"
        row-key="id"
        table-name="DatabaseItemDivisions"
        show-new-row
        show-row-actions
        @add-new-row="createDivision"
        @row-update="updateDivision"
        allow-import
      >
        <template #rowActions="props">
          <button
            class="cursor-pointer p-1 hover:bg-gray-300"
            title="delete"
            @click="deleteDivision(props.row.id)"
          >
            <svg
              class="size-4 text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"
              />
            </svg>
          </button>
        </template>
      </BaseDataTable>
    </div>
    <div v-else-if="activeTab === 'Subdivisions'" class="flex w-full flex-col">
      <BaseDataTable
        :rows="subdivisionRows"
        :columns="subdivisionColumns"
        :groups="subdivisionGroups"
        row-key="id"
        table-name="DatabaseItemSubdivisions"
        show-new-row
        show-row-actions
        @add-new-row="createDivision"
        @row-update="updateDivision"
        allow-import
      >
        <template #rowActions="props">
          <button
            class="cursor-pointer p-1 hover:bg-gray-300"
            title="delete"
            @click="deleteDivision(props.row.id)"
          >
            <svg
              class="size-4 text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"
              />
            </svg>
          </button>
        </template>
      </BaseDataTable>
    </div>
  </div>
</template>
